import { PrinterIcon } from '@heroicons/react/24/outline';
import { CSSProperties, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, LinkButton, Segmented, Select, Skeleton, SkeletonRegion, StateBlock } from '../../components/ui';
import { usePageTitle } from '../../hooks/usePageTitle';
import api from '../../services/api';
import type { Table } from '../../types/Table';
import { getTranslation } from '../../utils/translation.utils';
import '../../styles/print.css';
import type { LocationRecord } from './apiTypes';
import { QrView } from './components/QrView';
import { cardLabel, FORMAT_SPEC, PRINT_FORMATS, PRINT_TEXT, PrintFormat, TOGO_SPEC } from './printText';
import { codeUrl, locationUrl } from './qr';
import { useRequest } from './useRequest';

interface Card {
    key: string;
    url: string;
    /** Table label as printed; absent for an unlabelled code. */
    label?: string;
    alt: string;
}

const isFormat = (v: string | null): v is PrintFormat => PRINT_FORMATS.some((f) => f === v);

const chunk = <T,>(items: T[], size: number): T[][] => Array.from({ length: Math.ceil(items.length / size) }, (_, i) => items.slice(i * size, (i + 1) * size));

/**
 * Print view, rendered without the shell.
 * ?tables=all | CODE1,CODE2 prints table codes; without it the to-go / counter code is printed on its own large card.
 * ?format=a4-6 (six per A4, default) | a6-1 (one per A6). ?lang= picks the language of the table card text.
 */
const LocationQrPrint = () => {
    const { id = '' } = useParams<{ id: string }>();
    const { t } = useTranslation(['locations', 'common']);
    const [params, setParams] = useSearchParams();
    const tablesParam = params.get('tables');
    const requestedFormat = params.get('format');
    const format: PrintFormat = isFormat(requestedFormat) ? requestedFormat : 'a4-6';
    const togo = tablesParam === null;

    const { data, state, retry } = useRequest(`print:${id}:${tablesParam !== null}`, async () => {
        const location = await api.get<LocationRecord>(`/locations/${id}`).then((r) => r.data);
        const tables = tablesParam !== null ? await api.get<Table[]>(`/locations/${id}/tables`).then((r) => r.data) : [];
        return { location, tables };
    });
    usePageTitle(data ? t('print.title', { name: data.location.name }) : t('print.titleLoading'));

    const languages = useMemo(() => (data?.location.restaurant.supportedLanguages ?? []).filter((l) => l in PRINT_TEXT), [data]);
    const requestedLang = params.get('lang');
    const lang = requestedLang && languages.includes(requestedLang) ? requestedLang : (languages[0] ?? 'en');

    const cards: Card[] = useMemo(() => {
        if (!data) return [];
        if (tablesParam === null)
            return [
                {
                    key: 'location',
                    url: locationUrl(data.location.id),
                    alt: t('qr.locationAlt', { name: data.location.name }),
                },
            ];
        const wanted = tablesParam === 'all' ? null : new Set(tablesParam.split(','));
        return data.tables
            .filter((x) => wanted === null || wanted.has(x.code))
            .map((x) => ({
                key: x.id,
                url: codeUrl(data.location.id, x.code),
                ...(x.tableNumber ? { label: cardLabel(x.tableNumber, lang) } : {}),
                alt: x.tableNumber ? t('qr.tableAlt', { label: x.tableNumber }) : t('qr.codeAlt', { code: x.code }),
            }));
    }, [data, tablesParam, lang, t]);

    const update = (key: string, value: string) =>
        setParams(
            (prev) => {
                const p = new URLSearchParams(prev);
                p.set(key, value);
                return p;
            },
            { replace: true }
        );

    const backTo = `/locations/${id}?tab=qr`;
    const spec = togo ? { ...TOGO_SPEC, perPage: 1 } : FORMAT_SPEC[format];
    const text = PRINT_TEXT[lang] ?? PRINT_TEXT.en;
    const restaurant = data?.location.restaurant;
    const venue = restaurant ? getTranslation(restaurant.translations, 'name', restaurant.name, lang) : '';
    const logoUrl = restaurant?.logoUrl || null;

    return (
        <main className="mx-auto max-w-5xl p-4 sm:p-6 print:max-w-none print:p-0">
            <style>{`@page { size: ${spec.size}; margin: ${spec.margin}; }`}</style>
            <div className="no-print mb-6 flex flex-col gap-4">
                <LinkButton to={backTo} variant="link" className="self-start">
                    {t('print.back')}
                </LinkButton>
                <h1 className="font-display text-display-sm text-strong sm:text-display">{t('print.heading')}</h1>
                {data && (
                    <div className="flex flex-wrap items-end gap-4">
                        {!togo && (
                            <div className="flex flex-col gap-1.5">
                                <span className="text-small font-medium text-strong">{t('print.format')}</span>
                                <Segmented
                                    aria-label={t('print.format')}
                                    value={format}
                                    onChange={(v) => update('format', v)}
                                    options={PRINT_FORMATS.map((f) => ({
                                        value: f,
                                        label: t(`print.formats.${f}`),
                                    }))}
                                />
                            </div>
                        )}
                        {!togo && languages.length > 1 && (
                            <div className="flex w-56 flex-col gap-1.5">
                                <label htmlFor="print-lang" className="text-small font-medium text-strong">
                                    {t('print.language')}
                                </label>
                                <Select
                                    id="print-lang"
                                    value={lang}
                                    onChange={(v) => update('lang', v)}
                                    options={languages.map((l) => ({
                                        value: l,
                                        label: t(`print.languages.${l}`),
                                    }))}
                                />
                            </div>
                        )}
                        <Button
                            variant="primary"
                            onClick={() => window.print()}
                            disabled={cards.length === 0}
                            icon={<PrinterIcon className="size-5" aria-hidden="true" />}
                        >
                            {t('print.print')}
                        </Button>
                    </div>
                )}
                {data && cards.length > 0 && (
                    <p className="text-small text-muted">
                        {t('print.hint', {
                            cards: cards.length,
                            pages: Math.ceil(cards.length / spec.perPage),
                        })}
                    </p>
                )}
            </div>

            {state === 'error' && (
                <StateBlock
                    variant="error"
                    title={t('print.loadFailed')}
                    body={t('common:ui.checkConnection')}
                    action={<Button onClick={retry}>{t('common:ui.retry')}</Button>}
                />
            )}
            {state === 'loading' && (
                <SkeletonRegion>
                    <Skeleton className="h-96" />
                </SkeletonRegion>
            )}
            {data && cards.length === 0 && (
                <div className="no-print">
                    <StateBlock
                        variant="empty"
                        title={t('print.emptyTitle')}
                        body={t('print.emptyBody')}
                        action={<LinkButton to={backTo}>{t('print.back')}</LinkButton>}
                    />
                </div>
            )}
            {data && cards.length > 0 && (
                <div className="qr-preview" role="region" aria-label={t('print.preview')} tabIndex={0}>
                    <div className="qr-sheet" data-format={togo ? 'togo' : format} lang={lang} style={{ '--qr-size': `${spec.qrMm}mm` } as CSSProperties}>
                        {chunk(cards, spec.perPage).map((page, i) => (
                            <div key={i} className="qr-page">
                                {page.map((card) => (
                                    <div key={card.key} className="qr-card">
                                        <div className="qr-card__head">
                                            {logoUrl ? (
                                                <img src={logoUrl} alt={venue} className="qr-card__logo" />
                                            ) : (
                                                <div className="qr-card__venue">{venue}</div>
                                            )}
                                        </div>
                                        <QrView url={card.url} size={576} label={card.alt} className="qr-card__code" />
                                        <div className="qr-card__foot">
                                            {togo ? (
                                                (languages.length > 0 ? languages : ['en']).map((l) => (
                                                    <div key={l} lang={l} className="qr-card__togo">
                                                        {PRINT_TEXT[l]?.togo}
                                                    </div>
                                                ))
                                            ) : (
                                                <>
                                                    {card.label && <div className="qr-card__table">{card.label}</div>}
                                                    <div className="qr-card__scan">{text.scan}</div>
                                                </>
                                            )}
                                            <div className="qr-card__url">{togo ? card.url.replace(/^https?:\/\//, '') : card.url}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </main>
    );
};

export default LocationQrPrint;
